import { createSelector } from '@reduxjs/toolkit';
import {
  getMissingRequiredPatientFields,
  resolvePatientDataCollectionConfig,
} from '@aldebaran/common/util/patientDataCollection';
import { RootState } from '@/types/graph';
import { resolveBaseStepId } from './computeFlow';
import { COLLECTED_FIELD_STEPS, StepId } from './types';

export const selectOnboardingFlow = (state: RootState): string[] =>
  state.graph.onboardingFlow;

export const selectCurrentStepIndex = (state: RootState): number =>
  state.graph.currentStepIndex;

export const selectCurrentStep = createSelector(
  [selectOnboardingFlow, selectCurrentStepIndex],
  (flow, index): string | undefined => flow[index],
);

export const selectIsFirstStep = createSelector(
  [selectCurrentStepIndex],
  (index): boolean => index === 0,
);

export const selectIsLastStep = createSelector(
  [selectOnboardingFlow, selectCurrentStepIndex],
  (flow, index): boolean => index === flow.length - 1,
);

/**
 * The earliest step of the current flow that collects a field the practice
 * requires and the patient has not filled in, if any. Steps outside the flow
 * (e.g. dropped for an ineligible Mon Bilan Prévention patient) are ignored.
 */
export const selectMissingPatientDataStep = (
  state: RootState,
): StepId | undefined => {
  const patient = state.consultation.patient;
  if (!patient) return undefined;

  const missing = getMissingRequiredPatientFields(
    patient,
    resolvePatientDataCollectionConfig(
      state.consultation.practice?.patientDataCollectionConfig,
    ),
  );
  if (missing.length === 0) return undefined;

  const missingSteps = new Set(
    missing.map((key) => COLLECTED_FIELD_STEPS[key]),
  );
  return (selectOnboardingFlow(state) as StepId[]).find((step) =>
    missingSteps.has(resolveBaseStepId(step)),
  );
};
